import { Component, type ReactNode, type ErrorInfo } from "react";
import { Button } from "@/components/ui/button";

type Props = {
  routeLabel: string;
  children: ReactNode;
};

type State = {
  error: Error | null;
  componentStack: string | null;
};

/**
 * Route-level error boundary. Catches render-time crashes inside a single
 * route's component tree and renders a visible fallback INSTEAD of letting
 * the error propagate to the React root (where it would unmount the whole
 * MainLayout and leave the body's dark background showing through — i.e.
 * the "black screen on mobile" failure mode).
 *
 * The fallback intentionally surfaces the raw error message + the first
 * frame of the component stack on-screen. Operators usually can't open
 * mobile devtools, so dumping the error into the page is the only way to
 * see what actually broke from a phone. The full stack is also logged to
 * `console.error` so it shows up in desktop devtools and any browser
 * log capture.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    // Normalise non-Error throws (string, object, undefined) so the fallback
    // UI can always rely on `error.name` / `error.message` being present.
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    // eslint-disable-next-line no-console
    console.error(
      `[RouteErrorBoundary:${this.props.routeLabel}]`,
      error,
      info.componentStack,
    );
  }

  componentDidUpdate(prevProps: Props) {
    // Reset the captured error when the route changes (e.g. operator
    // navigates from one inventory item to another). Without this, a
    // sticky fallback would hide a healthy detail page just because a
    // prior id crashed.
    if (this.state.error && prevProps.routeLabel !== this.props.routeLabel) {
      this.setState({ error: null, componentStack: null });
    }
  }

  private reset = () => {
    this.setState({ error: null, componentStack: null });
  };

  private reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const err = this.state.error;
    const firstStackLine = (err.stack ?? "").split("\n").slice(0, 4).join("\n");
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
          <div className="text-base font-semibold text-destructive">
            Something went wrong loading this page
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            Route: {this.props.routeLabel}
          </div>
          <div className="mt-3 text-sm">
            <span className="font-medium">{err.name}:</span> {err.message}
          </div>
          {firstStackLine ? (
            <pre className="mt-3 text-xs whitespace-pre-wrap break-words bg-background/60 border rounded p-2 max-h-64 overflow-auto">
              {firstStackLine}
            </pre>
          ) : null}
          {this.state.componentStack ? (
            <pre className="mt-2 text-xs whitespace-pre-wrap break-words bg-background/60 border rounded p-2 max-h-64 overflow-auto">
              {this.state.componentStack.split("\n").slice(0, 8).join("\n")}
            </pre>
          ) : null}
          <div className="mt-4 flex gap-2">
            <Button size="sm" variant="default" onClick={this.reload}>
              Reload page
            </Button>
            <Button size="sm" variant="outline" onClick={this.reset}>
              Try again without reload
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
